/**
 * Draft-day tools. Admin only, and honest about what it does not know.
 *
 * Three things, in the order they depend on each other:
 *
 *   CONSENSUS ADP. Average of whatever respected sources have been loaded — Yahoo, ESPN, Sleeper,
 *   FantasyPros. Averaging is the point: any single source has house biases, and the spread
 *   between them is itself information (a player two sources disagree about by thirty picks is a
 *   player somebody is wrong about).
 *
 *   ADJUSTED ADP. Consensus is computed for a 12-team snake with no keepers. This league is ten
 *   teams, sixteen rounds, and roughly forty-six of the best players are already off the board as
 *   keepers before anybody picks. So a national ADP of 30 does not mean round 3 here. Adjusting
 *   means: drop everyone kept, re-rank what is left, and map that rank onto OUR 160 picks.
 *
 *   PROJECTION. What the next four rounds might look like — every remaining pick, filled by the
 *   best available player weighted by what that manager still needs and by what he has actually
 *   done in 23 drafts.
 *
 * The last one is a guess and is labelled a guess everywhere it appears. It is worth having
 * anyway: the useful output is not "Luke takes Player X", it is "three of the four running backs
 * you want are gone before your next pick", which is a question about the shape of the board
 * rather than about anybody's mind.
 */

import adpData from '../data/adp-2026.json';
import { BUNDLED_BANK, positionsOf, type Player, type PlayerBank } from './playerBank.js';
import { positionBias } from './tendencies.js';

/* -- the ADP file ------------------------------------------------------------------------------ */

export interface AdpRow {
  name: string;
  pos: string;
  team?: string;
  /** Average pick by source. A source that does not rank him is simply absent. */
  bySource: Record<string, number>;
}

/**
 * The format a source was drawn from, where it says so.
 *
 * DECLARED, NOT GUESSED, wherever the file carries it. A source that states its format in a
 * `format` field is believed; one that only states it in its own label is parsed with the regexes
 * below; one that states it nowhere is UNKNOWN, which is a third thing and not the same as
 * "matches" or "does not match".
 */
export interface SourceFormat {
  /** Teams in the drafts the ranking came from. Null when the source does not say. */
  teams: number | null;
  /** Points per reception. 0.5 here, 1 for full PPR, 0 for standard. Null when unstated. */
  ppr: number | null;
}

export interface AdpSource {
  id: string;
  label: string;
  readOn: string;
  players: number;
  format?: SourceFormat;
}

export interface AdpFile {
  season: number;
  /** When each source was read, so a stale one can be told from a fresh one. */
  sources: AdpSource[];
  players: AdpRow[];
}

const EMPTY: AdpFile = { season: 2026, sources: [], players: [] };
const BUNDLED = (adpData as unknown as AdpFile | null) ?? EMPTY;

let LOADED: AdpFile | null = null;
let VERSION = 0;
const watchers = new Set<() => void>();

export const adpVersion = (): number => VERSION;
export function onAdpChanged(fn: () => void): () => void {
  watchers.add(fn);
  return () => { watchers.delete(fn); };
}

export function loadAdp(raw: unknown): { players: number; sources: string[] } {
  const file = raw as AdpFile;
  if (!file || !Array.isArray(file.players) || !Array.isArray(file.sources)) {
    throw new Error('That is not an ADP export — it has no players and sources.');
  }
  LOADED = file;
  VERSION += 1;
  for (const w of watchers) w();
  return { players: file.players.length, sources: file.sources.map((s) => s.label) };
}

export function clearAdp(): void {
  LOADED = null;
  VERSION += 1;
  for (const w of watchers) w();
}

export const adpFile = (): AdpFile => LOADED ?? BUNDLED;
export const hasAdp = (): boolean => adpFile().players.length > 0;

/* -- consensus --------------------------------------------------------------------------------- */

export interface Consensus {
  name: string;
  pos: string;
  /** Mean of the sources that rank him. */
  adp: number;
  /** How many sources agreed to rank him at all — one source is not a consensus. */
  sources: number;
  /** Highest minus lowest. Big numbers are disagreements worth looking at. */
  spread: number;
  bySource: Record<string, number>;
}

/**
 * Merged at READ time, not just at fetch time.
 *
 * Sources disagree about suffixes — one file had "James Cook" and "James Cook III" as separate
 * men, and the projection duly drafted him twice, in the first round, to two different teams.
 * Stripping the suffix for the KEY (never for display) merges them. Doing it here rather than
 * only in the build script means an ADP file already on disk is fixed without re-fetching.
 */
/**
 * THE KEY A NAME IS LOOKED UP BY, exported because more than one screen now asks the consensus
 * where a man goes and every one of them has to ask the same question. Home sorted a roster by
 * ADP and put `Amon-Ra St. Brown` last, because his pick was saved from a source that spells him
 * `Amon-Ra St Brown` and a raw string compare is not a name match.
 */
export const adpKey = (name: string) =>
  norm(name).replace(/\b(jr|sr|ii|iii|iv|v)\b/g, '').replace(/\s+/g, ' ').trim();

/** Where the consensus takes each man, by `adpKey`. Absent means nobody ranks him. */
export function adpByName(file: AdpFile = adpFile()): Map<string, number> {
  const out = new Map<string, number>();
  for (const c of consensus(file)) {
    const k = adpKey(c.name);
    if (!out.has(k) || c.adp < out.get(k)!) out.set(k, c.adp);
  }
  return out;
}

const mergeKey = (name: string, pos: string) =>
  `${norm(name).replace(/\b(jr|sr|ii|iii|iv|v)\b/g, '').replace(/\s+/g, ' ').trim()}|${pos}`;

export function consensus(file: AdpFile = adpFile()): Consensus[] {
  const merged = new Map<string, { name: string; pos: string; bySource: Record<string, number> }>();
  for (const r of file.players) {
    const key = mergeKey(r.name, r.pos);
    const had = merged.get(key);
    if (!had) {
      merged.set(key, { name: r.name, pos: r.pos, bySource: { ...r.bySource } });
      continue;
    }
    // Keep the longer spelling — "James Cook III" identifies him better than "James Cook".
    if (r.name.length > had.name.length) had.name = r.name;
    for (const [src, pick] of Object.entries(r.bySource)) {
      // A source that named him twice is a source confused about him; take the earlier pick,
      // which is the one that matters for when he actually goes.
      had.bySource[src] = Math.min(had.bySource[src] ?? Infinity, pick);
    }
  }

  const out: Consensus[] = [];
  for (const r of merged.values()) {
    const picks = Object.values(r.bySource).filter((n) => Number.isFinite(n) && n > 0);
    if (picks.length === 0) continue;
    out.push({
      name: r.name,
      pos: r.pos,
      adp: picks.reduce((a, b) => a + b, 0) / picks.length,
      sources: picks.length,
      spread: Math.max(...picks) - Math.min(...picks),
      bySource: r.bySource,
    });
  }
  return out.sort((a, b) => a.adp - b.adp);
}

/* -- how relevant is a source to THIS league? -------------------------------------------------
 *
 * The league is ten teams and half PPR, permanently, since 2016-IV-1. A twelve-team full-PPR
 * ranking is not wrong, it is answering a different question — receivers are worth more per
 * reception and there are two more teams competing for them, so the same player goes earlier
 * there than he does here. Averaging such a source in at equal weight quietly imports somebody
 * else's format into our keeper maths.
 *
 * So sources are WEIGHTED rather than filtered. Filtering to exact matches leaves one thin source
 * and calls it a consensus; weighting keeps the breadth and still lets the source that matches
 * our rules speak loudest. Every weight is shown on the screen with the reason it got, because a
 * weighted average nobody can audit is just a number with extra steps.
 */

/** What the rulebook makes this league. Half PPR is permanent — 2016-IV-1. */
export const LEAGUE_FORMAT: Readonly<SourceFormat> = { teams: 10, ppr: 0.5 };

/**
 * Read a format out of a source label, for files written before `format` existed.
 *
 * Deliberately conservative: it only claims a value the label actually spells out. "Sleeper (real
 * drafts)" yields nothing at all, which is the correct answer — real Sleeper drafts are mostly
 * twelve-team full PPR but the file does not say so, and inferring it from a hunch would be this
 * module inventing evidence.
 */
export function formatFromLabel(label: string): SourceFormat {
  const l = label.toLowerCase();
  const teams = l.match(/(\d{1,2})[- ]team/);
  const ppr = /half[- ]?(point )?ppr|0\.5 ?ppr/.test(l) ? 0.5
    : /\bfull[- ]?ppr\b/.test(l) ? 1
      : /\bstandard\b|\bnon[- ]?ppr\b/.test(l) ? 0
        : /\bppr\b/.test(l) ? 1
          : null;
  return { teams: teams ? Number(teams[1]) : null, ppr };
}

export const formatOf = (s: AdpSource): SourceFormat => s.format ?? formatFromLabel(s.label);

export interface SourceWeight {
  id: string;
  label: string;
  format: SourceFormat;
  weight: number;
  /** Why it got that weight, in the words the screen prints. */
  why: string;
}

/**
 * A known mismatch scores BELOW an unknown, and that ordering is the whole design.
 *
 * An unstated format is uncertainty — the source might be a ten-team half-PPR board for all we
 * know. A stated twelve-team full-PPR board is a fact about it being answerable to different
 * rules. Ranking uncertainty above a known mismatch would mean a source is better off saying
 * nothing, which is precisely backwards.
 */
export function weightOf(fmt: SourceFormat, league: SourceFormat = LEAGUE_FORMAT): SourceWeight['weight'] {
  const score = (mine: number | null, ours: number | null): number => {
    if (mine === null || ours === null) return 0;      // unknown
    return mine === ours ? 1 : -1;                     // match / mismatch
  };
  const t = score(fmt.teams, league.teams);
  const p = score(fmt.ppr, league.ppr);
  const sum = t + p;
  if (sum >= 2) return 2;        // both stated and both match
  if (sum === 1) return 1.5;     // one matches, the other unstated
  if (sum <= -1) return 0.5;     // something is stated and disagrees
  return 1;                      // says nothing either way
}

export function sourceWeights(file: AdpFile = adpFile()): SourceWeight[] {
  return file.sources.map((s) => {
    const format = formatOf(s);
    const weight = weightOf(format);
    const said = [
      format.teams === null ? null : `${format.teams}-team`,
      format.ppr === null ? null
        : format.ppr === 0.5 ? 'half PPR' : format.ppr === 1 ? 'full PPR' : 'standard',
    ].filter(Boolean).join(', ');
    const why = weight === 2 ? `matches the league — ${said}`
      : weight === 1.5 ? `${said}, the rest unstated`
        : weight === 0.5 ? `${said}, which is not this league`
          : 'states no format, so it is taken at face value';
    return { id: s.id, label: s.label, format, weight, why };
  });
}

/**
 * The same consensus, with each source pulling in proportion to how much it answers our rules.
 *
 * `sources` and `spread` still measure the RAW evidence — how many opinions there are and how far
 * apart they sit are facts about the sources, not about the weighting, and folding the weight
 * into them would hide a real disagreement behind a confident-looking average.
 */
export function weightedConsensus(file: AdpFile = adpFile()): Consensus[] {
  const w = new Map(sourceWeights(file).map((s) => [s.id, s.weight]));
  return consensus(file).map((c) => {
    let num = 0;
    let den = 0;
    for (const [src, pick] of Object.entries(c.bySource)) {
      if (!Number.isFinite(pick) || pick <= 0) continue;
      const weight = w.get(src) ?? 1;
      num += pick * weight;
      den += weight;
    }
    return den === 0 ? c : { ...c, adp: num / den };
  }).sort((a, b) => a.adp - b.adp);
}

/* -- adjusted to our board --------------------------------------------------------------------- */

export interface Adjusted extends Consensus {
  /** Where he actually goes here, once keepers are gone: 1 = first pick of our draft. */
  ourPick: number;
  ourRound: number;
  /** ourPick − adp. Negative means he lasts longer here than nationally. */
  drift: number;
}

/**
 * Re-rank a national ADP onto this league's board.
 *
 * Two corrections, and the keeper one dominates. Forty-odd of the best players never reach the
 * draft, so everybody behind them moves up by however many were taken ahead of them — a receiver
 * ranked 60th nationally with 30 keepers above him is really the 30th player available here. The
 * second correction is size: ten teams rather than twelve means a round is ten picks, so the same
 * rank lands in a later round than the national number implies.
 */
export function adjusted(
  keptPlayers: readonly string[],
  teams = 10,
  file: AdpFile = adpFile(),
): Adjusted[] {
  const kept = new Set(keptPlayers.map(norm));
  const available = consensus(file).filter((c) => !kept.has(norm(c.name)));
  return available.map((c, i) => {
    const ourPick = i + 1;
    return {
      ...c,
      ourPick,
      ourRound: Math.floor(i / teams) + 1,
      drift: ourPick - c.adp,
    };
  });
}

/* -- what a team still needs ------------------------------------------------------------------- */

/** The 2026 starting lineup. Three flexes is what makes this league a receiver league. */
/** What may fill a W/R/T slot. */
export const FLEX_ELIGIBLE = ['RB', 'WR', 'TE'] as const;

export const STARTERS: ReadonlyArray<{ pos: string; n: number }> = [
  { pos: 'QB', n: 1 }, { pos: 'RB', n: 2 }, { pos: 'WR', n: 2 },
  { pos: 'TE', n: 1 }, { pos: 'FLEX', n: 3 }, { pos: 'DEF', n: 1 },
];

/**
 * Is a roster below the starting requirement at this position?
 *
 * The 8a counts grid paints exactly this amber, and the line it paints against is the starting
 * lineup, not zero. One receiver in a two-receiver league is a hole the same way none is: the
 * drawing puts a `1` under WR in status ink and a `1` under TE in plain ink, which only reads
 * if the number is being compared against the slots. FLEX has no column, so it has no say.
 */
export function belowStarters(pos: string, have: number): boolean {
  const s = STARTERS.find((x) => x.pos === pos && x.pos !== 'FLEX');
  return s ? have < s.n : false;
}

export interface Need {
  pos: string;
  have: number;
  /** How many the starting lineup asks for. Carried so a screen can say "1 of 2", not just "short". */
  need: number;
  /** How badly, 0-1. Drives both the projection and the colour on screen. */
  urgency: number;
  why: string;
}

/**
 * What a roster is short of, given who is already on it.
 *
 * Starters first and flex last, because a manager with three receivers and no quarterback needs a
 * quarterback in a way that somebody's fourth receiver never needs anything. Kickers are ignored:
 * this league does not start one, and pretending otherwise put a kicker in every projection.
 */
export function needs(roster: readonly string[], bank: PlayerBank = BUNDLED_BANK): Need[] {
  const byName = new Map(bank.players.map((p) => [norm(p.name), p]));
  const have = new Map<string, number>();
  for (const name of roster) {
    const p = byName.get(norm(name));
    if (!p) continue;
    for (const pos of positionsOf(p)) have.set(pos, (have.get(pos) ?? 0) + 1);
  }

  const out: Need[] = [];
  for (const s of STARTERS) {
    if (s.pos === 'FLEX') {
      const flexish = FLEX_ELIGIBLE.reduce((n, p) => n + (have.get(p) ?? 0), 0);
      const startersUsed = 2 + 2 + 1; // the dedicated RB/WR/TE slots
      const spare = Math.max(0, flexish - startersUsed);
      out.push({
        pos: 'FLEX',
        have: spare,
        need: s.n,
        urgency: Math.max(0, Math.min(1, (s.n - spare) / s.n)),
        why: `${spare} of ${s.n} flex spots covered beyond the dedicated slots.`,
      });
      continue;
    }
    const got = have.get(s.pos) ?? 0;
    out.push({
      pos: s.pos,
      have: got,
      need: s.n,
      urgency: Math.max(0, Math.min(1, (s.n - got) / s.n)),
      why: got >= s.n
        ? `${s.pos} covered — ${got} for ${s.n} slot${s.n === 1 ? '' : 's'}.`
        : `Needs ${s.n - got} more ${s.pos}.`,
    });
  }
  return out.sort((a, b) => b.urgency - a.urgency);
}

/* -- the projection ---------------------------------------------------------------------------- */

export interface ProjectedPick {
  overall: number;
  round: number;
  gm: string;
  player: string;
  pos: string;
  /** 0-1. How much of the field this pick beat — not a probability, a margin. */
  confidence: number;
  why: string;
}

export interface Projection {
  picks: ProjectedPick[];
  /** Every reason the answer might be wrong, in the app's own words. */
  caveats: string[];
}

/**
 * Fill the next N rounds, pick by pick.
 *
 * The scoring is deliberately simple and deliberately explained on screen, because a projection
 * nobody can argue with is a projection nobody should trust. A player's score is his position on
 * the adjusted board, moved by two things: how badly the man picking still needs that position,
 * and how much more often than the league he has taken that position early across 23 drafts.
 *
 * It is greedy — every manager takes the best thing on his own board — which is exactly wrong
 * about the interesting cases (nobody reaches, nobody hoards) and roughly right about the shape.
 * The shape is the useful part.
 */
export function project(
  upcoming: ReadonlyArray<{ overall: number; round: number; gm: string }>,
  rostersByGm: Readonly<Record<string, readonly string[]>>,
  keptPlayers: readonly string[],
  rounds = 4,
  bank: PlayerBank = BUNDLED_BANK,
): Projection {
  const caveats: string[] = [];
  const board = adjusted(keptPlayers);
  if (board.length === 0) {
    return {
      picks: [],
      caveats: ['No ADP has been loaded, so there is no board to project from.'],
    };
  }

  const sources = adpFile().sources.length;
  if (sources < 2) {
    caveats.push(
      sources === 1
        ? 'Only one ADP source is loaded, so "consensus" is that source\'s opinion.'
        : 'No ADP sources are recorded on this file.',
    );
  }

  const firstRound = upcoming[0]?.round ?? 1;
  const window = upcoming.filter((p) => p.round < firstRound + rounds);
  const taken = new Set<string>();
  const rosters = new Map<string, string[]>(
    Object.entries(rostersByGm).map(([gm, r]) => [gm, [...r]]),
  );
  const byName = new Map(bank.players.map((p) => [norm(p.name), p]));
  const picks: ProjectedPick[] = [];

  for (const slot of window) {
    const roster = rosters.get(slot.gm) ?? [];
    const need = new Map(needs(roster, bank).map((n) => [n.pos, n.urgency]));

    let best: { row: Adjusted; score: number; runnerUp: number } | null = null;
    let second = 0;
    for (const row of board) {
      if (taken.has(norm(row.name))) continue;
      // Only the top of the board is ever in contention; scanning further is wasted work and
      // lets a wild bias promote somebody nobody would take.
      if (best && row.ourPick > best.row.ourPick + 40) break;

      const p = byName.get(norm(row.name));
      const positions = p ? positionsOf(p) : [row.pos];
      const urgency = Math.max(...positions.map((x) => need.get(x) ?? need.get('FLEX') ?? 0), 0);
      const bias = Math.max(...positions.map((x) => positionBias(slot.gm, x, slot.round)));

      // Base falls with board position; need and habit multiply it.
      const score = (1 / row.ourPick) * (1 + urgency) * bias;
      if (!best || score > best.score) {
        second = best?.score ?? 0;
        best = { row, score, runnerUp: second };
      } else if (score > second) {
        second = score;
      }
    }
    if (!best) break;

    taken.add(norm(best.row.name));
    rosters.set(slot.gm, [...roster, best.row.name]);
    const urgency = need.get(best.row.pos) ?? 0;
    picks.push({
      overall: slot.overall,
      round: slot.round,
      gm: slot.gm,
      player: best.row.name,
      pos: best.row.pos,
      confidence: second > 0 ? Math.max(0, Math.min(1, 1 - second / best.score)) : 0.5,
      why: [
        `board ${best.row.ourPick}`,
        urgency > 0.4 ? `needs ${best.row.pos}` : null,
        positionBias(slot.gm, best.row.pos, slot.round) > 1.25 ? `takes ${best.row.pos} early` : null,
      ].filter(Boolean).join(' · '),
    });
  }

  caveats.push(
    'Greedy: everybody takes the best player on his own board. Real drafts have reaches and runs.',
    'Positional habits come from 23 drafts, but only the picks whose players the bank still '
    + 'recognises — mostly recent seasons.',
  );
  return { picks, caveats };
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();

/**
 * Rows where the sources disagree most — the arguments worth having before draft night.
 *
 * Restricted to players anybody would actually draft, and this restriction is the whole thing.
 * The sources do not share a scale in the tail: Sleeper ranks 794 players, ESPN's average draft
 * position runs out around 300, and a man ranked 700th by one and unranked by the other produces
 * a "spread" of 600 that means nothing except that one list is longer. Left unfiltered the panel
 * was entirely fourth-string tight ends.
 *
 * Inside the draftable range the scales agree well enough for a gap to be real.
 */
export function disagreements(file: AdpFile = adpFile(), n = 15, within = 150): Consensus[] {
  return consensus(file)
    .filter((c) => c.sources >= 2 && c.adp <= within)
    .filter((c) => Math.min(...Object.values(c.bySource)) <= within)
    .sort((a, b) => b.spread - a.spread)
    .slice(0, n);
}

export type { Player };
